(function () {
  const STORAGE_KEY = "regional-free-shipping-region";
  const CACHE_TTL = 24 * 60 * 60 * 1000;
  const IP_LOOKUP_URL = "https://ipapi.co/json/";
  const IP_LOOKUP_TIMEOUT = 4000;
  const CONTAINER_SELECTOR = "[data-regional-shipping]";
  const CART_EVENTS = ["cart:update", "cart:updated", "cart:refresh", "theme:cart:updated"];

  const GROUPS_BY_STATE = {
    AC: "north",
    AP: "north",
    AM: "north",
    PA: "north",
    RO: "north",
    RR: "north",
    TO: "north",
    AL: "northeast",
    BA: "northeast",
    CE: "northeast",
    MA: "northeast",
    PB: "northeast",
    PE: "northeast",
    PI: "northeast",
    RN: "northeast",
    SE: "northeast",
    SP: "southeast",
    RJ: "southeast",
    MG: "southeast",
    ES: "southeast",
    MT: "midwest",
    MS: "midwest",
    GO: "midwest",
    DF: "midwest",
    RS: "south",
    SC: "south",
    PR: "south",
  };

  const DEFAULT_THRESHOLDS = {
    north: 799,
    northeast: 799,
    southeast: 499,
    midwest: 499,
    south: 499,
  };

  let initialized = false;
  let status = "idle";
  let containers = [];
  let currentRegion = null;
  let detectPromise = null;
  const drawerListeners = new WeakSet();

  const moneyFormatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const api = {
    init,
    refreshContainers,
    forceRender,
    setRegionFromState,
  };

  window.RegionalFreeShipping = api;

  function init() {
    if (initialized) {
      refreshContainers();
      forceRender();
      return;
    }

    initialized = true;
    bindGlobalEvents();
    refreshContainers();

    const cachedRegion = readCachedRegion();
    if (cachedRegion) {
      currentRegion = cachedRegion;
      status = "ready";
      forceRender();
      return;
    }

    status = "loading";
    hideContainers();
    detectRegionByIp();
  }

  function refreshContainers() {
    containers = Array.from(document.querySelectorAll(CONTAINER_SELECTOR));
    bindDrawerOpenEvents();

    if (status === "unavailable") {
      hideContainers();
    }

    return containers;
  }

  function forceRender() {
    refreshContainers();

    if (!containers.length) return;

    if (!currentRegion) {
      hideContainers();
      return;
    }

    containers.forEach(renderContainer);
  }

  function setRegionFromState(state, metadata) {
    const normalizedState = normalizeState(state);
    const group = getGroupForState(normalizedState);

    if (!group) {
      currentRegion = null;
      status = "unavailable";
      removeCachedRegion();
      hideContainers();
      return false;
    }

    currentRegion = {
      state: normalizedState,
      group,
      metadata: metadata || {},
      updatedAt: Date.now(),
      expiresAt: Date.now() + CACHE_TTL,
    };

    status = "ready";
    writeCachedRegion(currentRegion);
    forceRender();

    return true;
  }

  function renderContainer(container) {
    const message = container.querySelector("[data-regional-shipping-message]");
    const bar = container.querySelector("[data-regional-shipping-bar]");

    if (!message || !bar || !currentRegion) return;

    if (!isRegionEnabled(container, currentRegion.group)) {
      hideContainer(container);
      return;
    }

    const cartTotalCents = parseCents(container.dataset.cartTotalCents);
    const thresholdCents = getThresholdCents(container, currentRegion.group);
    const remainingCents = thresholdCents - cartTotalCents;
    const progressPercent = thresholdCents > 0 ? Math.min(Math.max((cartTotalCents / thresholdCents) * 100, 0), 100) : 100;

    container.hidden = false;
    container.classList.remove("hidden");
    message.classList.remove("is-loading");
    bar.style.setProperty("--progress-percent", `${progressPercent.toFixed(2)}%`);

    if (remainingCents <= 0) {
      container.classList.add("is-complete");
      message.innerHTML = "Parabéns! Você ganhou <strong>frete grátis</strong>.";
      return;
    }

    container.classList.remove("is-complete");
    message.innerHTML = `Faltam <strong>${formatMoney(remainingCents)}</strong> para ganhar <strong>frete grátis</strong>.`;
  }

  function hideContainers() {
    containers.forEach((container) => {
      hideContainer(container);
    });
  }

  function hideContainer(container) {
    const message = container.querySelector("[data-regional-shipping-message]");
    const bar = container.querySelector("[data-regional-shipping-bar]");

    container.hidden = true;
    container.classList.add("hidden");
    container.classList.remove("is-complete");

    if (message) {
      message.classList.add("is-loading");
      message.textContent = "";
    }

    if (bar) {
      bar.style.setProperty("--progress-percent", "0%");
    }
  }

  async function detectRegionByIp() {
    if (detectPromise) return detectPromise;

    detectPromise = (async () => {
      let timeout;
      const controller = typeof AbortController === "function" ? new AbortController() : null;

      try {
        const request = fetch(IP_LOOKUP_URL, {
          ...(controller ? { signal: controller.signal } : {}),
          credentials: "omit",
          cache: "no-store",
        });

        const response = await Promise.race([
          request,
          new Promise((_, reject) => {
            timeout = window.setTimeout(() => {
              controller?.abort();
              reject(new Error("IP lookup timeout"));
            }, IP_LOOKUP_TIMEOUT);
          }),
        ]);

        if (!response.ok) throw new Error("IP lookup failed");

        const data = await response.json();
        const country = normalizeCountry(data.country_code || data.country);

        if (country !== "BR" && country !== "BRA") throw new Error("IP lookup outside Brazil");

        const state = data.region_code;
        const regionWasSet = setRegionFromState(state, {
          source: "ip",
          state,
        });

        if (!regionWasSet) throw new Error("IP lookup without a valid state");
      } catch (error) {
        if (!currentRegion) {
          status = "unavailable";
          hideContainers();
        }
      } finally {
        if (timeout) window.clearTimeout(timeout);
        detectPromise = null;
      }
    })();

    return detectPromise;
  }

  function getThresholdCents(container, group) {
    const attributeByGroup = {
      north: "thresholdNorth",
      northeast: "thresholdNortheast",
      southeast: "thresholdSoutheast",
      midwest: "thresholdMidwest",
      south: "thresholdSouth",
    };

    const rawValue = container.dataset[attributeByGroup[group]];
    const fallback = DEFAULT_THRESHOLDS[group] || DEFAULT_THRESHOLDS.southeast;
    const threshold = parseCurrencyNumber(rawValue, fallback);

    return Math.round(threshold * 100);
  }

  function isRegionEnabled(container, group) {
    const attributeByGroup = {
      north: "regionEnabledNorth",
      northeast: "regionEnabledNortheast",
      southeast: "regionEnabledSoutheast",
      midwest: "regionEnabledMidwest",
      south: "regionEnabledSouth",
    };

    const enabledValue = container.dataset[attributeByGroup[group]];

    return enabledValue !== "false";
  }

  function parseCurrencyNumber(value, fallback) {
    if (value == null || String(value).trim() === "") return fallback;

    let normalized = String(value).trim().replace(/\s/g, "");
    const commaIndex = normalized.lastIndexOf(",");
    const dotIndex = normalized.lastIndexOf(".");

    if (commaIndex > -1 && dotIndex > -1) {
      if (commaIndex > dotIndex) {
        normalized = normalized.replace(/\./g, "").replace(",", ".");
      } else {
        normalized = normalized.replace(/,/g, "");
      }
    } else if (commaIndex > -1) {
      normalized = normalized.replace(",", ".");
    }

    const parsedValue = Number.parseFloat(normalized.replace(/[^\d.-]/g, ""));

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
  }

  function parseCents(value) {
    const parsedValue = Number.parseInt(value || "0", 10);

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
  }

  function normalizeState(state) {
    return String(state || "").trim().toUpperCase();
  }

  function normalizeCountry(country) {
    return String(country || "").trim().toUpperCase();
  }

  function getGroupForState(state) {
    return GROUPS_BY_STATE[state] || null;
  }

  function formatMoney(cents) {
    return moneyFormatter.format(cents / 100).replace(/\u00a0/g, " ");
  }

  function readCachedRegion() {
    try {
      const cachedValue = window.localStorage.getItem(STORAGE_KEY);
      if (!cachedValue) return null;

      const cachedRegion = JSON.parse(cachedValue);
      if (!cachedRegion || cachedRegion.expiresAt <= Date.now()) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      const state = normalizeState(cachedRegion.state);
      const group = getGroupForState(state);

      if (!group) return null;

      return {
        ...cachedRegion,
        state,
        group,
      };
    } catch (error) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  function writeCachedRegion(region) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(region));
    } catch (error) {
    }
  }

  function removeCachedRegion() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
    }
  }

  function bindGlobalEvents() {
    CART_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, scheduleRender);
    });

    window.addEventListener("pageshow", scheduleRender);
  }

  function bindDrawerOpenEvents() {
    document.querySelectorAll("cart-drawer-component").forEach((drawer) => {
      if (drawerListeners.has(drawer)) return;

      drawer.addEventListener("dialog:open", scheduleRender);
      drawerListeners.add(drawer);
    });
  }

  function scheduleRender() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        refreshContainers();
        forceRender();
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
